import type { Bookmark } from "@repo/fika-shared/src/loader/bookmark-schema.js"
import type { Entry } from "@repo/fika-shared/src/loader/entry-schema.js"
import type { Log } from "@repo/fika-shared/src/loader/log-schema.js"
import type { Post } from "@repo/fika-shared/src/loader/post-schema.js"
import type { PostStat } from "@repo/fika-shared/src/loader/post-stat-schema.js"
import type { Publisher } from "@repo/fika-shared/src/loader/publisher-schema.js"
import type { RecentPost } from "@repo/fika-shared/src/loader/recent-post-schema.js"
import type { RecentPublisher } from "@repo/fika-shared/src/loader/recent-publisher-schema.js"
import type { Story } from "@repo/fika-shared/src/loader/story-schema.js"
import type { Subscription } from "@repo/fika-shared/src/loader/subscription-schema.js"
import type { Tag } from "@repo/fika-shared/src/loader/tags-schema.js"
import type { User } from "@repo/fika-shared/src/loader/user-schema.js"
import { ReactiveMap as RMap } from "@solid-primitives/map"
import { ReactiveSet as RSet } from "@solid-primitives/set"
import { createStore, produce } from "solid-js/store"

export const db = {
  story: createDatabase<Story>({
    indexes: ["feed_url", "dc_created_at", "url"],
  }),
  log: createDatabase<Log>({
    indexes: [],
  }),
  subscription: createDatabase<Subscription>({
    indexes: ["publisher_id"],
  }),
  entry: createDatabase<Entry>({
    indexes: ["story_id", "publisher_id", "published_at"],
  }),
  bookmark: createDatabase<Bookmark>({
    indexes: ["story_id", "bookmarked_at"],
  }),
  publisher: createDatabase<Publisher>({
    indexes: ["url", "user_id"],
  }),
  recent_publisher: createDatabase<RecentPublisher>({
    indexes: ["publisher_id"],
  }),
  recent_post: createDatabase<RecentPost>({
    indexes: ["post_id"],
  }),
  user: createDatabase<User>({
    indexes: [],
  }),
  post: createDatabase<Post>({
    indexes: ["published_at", "bookmark_id", "latest", "root_id"],
  }),
  postStat: createDatabase<PostStat>({
    indexes: ["id"],
  }),
  tag: createDatabase<Tag>({
    indexes: ["target_id", "target_type", "tag"],
  }),
} as const
export type DB = typeof db
;(globalThis as any).__db = db

type Id = string
type Indexes<Type, Attr extends keyof Type> = Map<
  Attr,
  RMap<Type[Attr], RSet<Id>>
>
type ById<Type> = { [id: Id]: Type }

export function createDatabase<Type extends { id: string }>(config: {
  indexes: (keyof Type)[]
}) {
  const [byId, setById] = createStore({} as ById<Type>)
  const indexes: Indexes<Type, keyof Type> = new Map(
    config.indexes.map(i => [i, new RMap()]),
  )

  function del(id: Id) {
    const entity = get(id)
    if (!entity) return

    setById(
      produce(byId => {
        delete byId[id]
      }),
    )

    for (const indexName of config.indexes) {
      const index = indexes.get(indexName)!
      const value = entity[indexName]

      index.get(value)!.delete(id)
    }
  }

  function upsert(id: Id, newEntity: Type) {
    const oldEntity = get(id)

    for (const indexName of config.indexes) {
      const index = indexes.get(indexName)!
      const newValue = newEntity[indexName]

      if (oldEntity) {
        const oldValue = oldEntity[indexName]

        if (oldValue !== newValue) {
          index.get(oldValue)!.delete(id)
        }
      }
      const entities = index.get(newValue)

      if (entities) {
        entities.add(id)
      } else {
        index.set(newValue, new RSet([id]))
      }
    }

    setById(id, newEntity)
  }

  function filterBy<K extends keyof Type>(query: Record<K, Type[K]>): Type[] {
    let entities
    const values = Object.entries(query) as Array<
      [keyof Type, Type[keyof Type]]
    >

    for (const [attr, value] of values) {
      const entitySet = indexes.get(attr)!.get(value)
      if (!entitySet || entitySet.size === 0) return []

      entities = entities ? entitySet.intersection(entities) : entitySet
    }

    if (!entities) return []
    return [...entities].map(id => get(id)!)
  }

  function sortBy(attr: keyof Type, order: "asc" | "desc") {
    const index = indexes.get(attr)!
    return Array.from(index)
      .sort((a, b) => {
        let comp

        if (typeof a[0] === "string" && typeof b[0] === "string") {
          comp = a[0].localeCompare(b[0])
        } else if (typeof a[0] === "number" && typeof b[0] === "number") {
          comp = a[0] - b[0]
        } else if (a[0] instanceof Date && b[0] instanceof Date) {
          comp = a[0].getTime() - b[0].getTime()
        } else if (a[0] === null || a[0] === undefined) {
          comp = -1
        } else if (b[0] === null || b[0] === undefined) {
          comp = 1
        } else {
          comp = 0
        }

        return order === "asc" ? comp : -comp
      })
      .flatMap(([_, set]) => [...set].map(get).filter(v => !!v))
  }

  function findBy<K extends keyof Type>(
    query: Record<K, Type[K]>,
  ): Type | null {
    return filterBy(query)[0] || null
  }

  function all() {
    return Object.values(byId)
  }

  function get(id: Id | null) {
    if (!id) return null
    return byId[id] || null
  }

  function empty() {
    return length() === 0
  }

  function length() {
    return Object.keys(byId).length
  }

  return {
    upsert,
    del,
    filterBy,
    findBy,
    sortBy,
    all,
    get,
    empty,
    length,
    indexes,
  }
}
